import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiClock,
  FiMessageSquare,
  FiSend,
  FiShield,
  FiChevronRight,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import escalationService from "../../services/escalationService";

const getStatusBadge = (status) => {
  const map = {
    submitted: { bg: "#e8f1f8", text: "#002747", border: "#b6d5ec", label: "Submitted" },
    under_review: { bg: "#fef3c7", text: "#b45309", border: "#fde68a", label: "Under Review" },
    action_taken: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", label: "Action Taken" },
    in_progress: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", label: "In Progress" },
    approved: { bg: "#ecfdf5", text: "#047857", border: "#a7f3d0", label: "Approved" },
    partially_approved: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", label: "Partially Approved" },
    rejected: { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca", label: "Rejected" },
    resolved: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", label: "Resolved" },
    closed: { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: "Closed" },
  };
  const config = map[status] || { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: status };
  return (
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: config.text }} />
      {config.label}
    </span>
  );
};

const getPriorityBadge = (priority) => {
  const map = {
    urgent: { bg: "#fee2e2", text: "#991b1b", label: "Urgent" },
    high: { bg: "#ffedd5", text: "#9a3412", label: "High" },
    medium: { bg: "#e0f2fe", text: "#075985", label: "Medium" },
    low: { bg: "#f1f5f9", text: "#475569", label: "Low" },
  };
  const c = map[priority] || map.medium;
  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        padding: "3px 8px",
        borderRadius: "4px",
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {c.label} Priority
    </span>
  );
};

export default function EscalationDetail() {
  const { id } = useParams();
  const { user } = useApp();
  const isSuperAdmin = user?.role === "superadmin";

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  // Super Admin Action Form State
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [actionStatus, setActionStatus] = useState("action_taken");
  const [actionDecision, setActionDecision] = useState("approved");
  const [actionTakenText, setActionTakenText] = useState("");
  const [allocatedResourcesText, setAllocatedResourcesText] = useState("");
  const [timelineMessageText, setTimelineMessageText] = useState("");
  const [savingAction, setSavingAction] = useState(false);

  const fetchRequest = useCallback(async () => {
    try {
      setLoading(true);
      const res = await escalationService.getById(id);
      const data = res?.data || res;
      if (data && (res?.success || data._id)) {
        setRequest(data);
        setActionStatus(data.status || "action_taken");
        setActionDecision(data.superAdminAction?.decision || "approved");
        setActionTakenText(data.superAdminAction?.actionTaken || "");
        setAllocatedResourcesText(data.superAdminAction?.allocatedResources || "");
      }
    } catch (error) {
      console.error("Failed to load escalation detail:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to load request details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  // Submit comment in timeline
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      setSendingComment(true);
      const res = await escalationService.addComment(id, commentText);
      const data = res?.data || res;
      if (data && (res?.success || data._id)) {
        toast.success("Message added to timeline.");
        setCommentText("");
        setRequest(data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to add comment.");
    } finally {
      setSendingComment(false);
    }
  };

  // Submit Super Admin Action
  const handleSaveAction = async (e) => {
    e.preventDefault();
    if (!actionStatus) {
      toast.error("Please select a status.");
      return;
    }

    try {
      setSavingAction(true);
      const payload = {
        status: actionStatus,
        decision: actionDecision,
        actionTaken: actionTakenText.trim(),
        allocatedResources: allocatedResourcesText.trim(),
        timelineMessage: timelineMessageText.trim(),
      };

      const res = await escalationService.takeAction(id, payload);
      const data = res?.data || res;
      if (data && (res?.success || data._id)) {
        toast.success("Super Admin action saved successfully!");
        setRequest(data);
        setShowActionPanel(false);
        setTimelineMessageText("");
      }
    } catch (error) {
      console.error("Save action error:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to save action.");
    } finally {
      setSavingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ padding: "4rem 1rem", textAlign: "center" }}>
        <div className="spinner" style={{ margin: "0 auto 12px" }} />
        <div style={{ color: "#64748b", fontSize: "14px" }}>Loading request details…</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="page-container" style={{ padding: "3rem 1rem", textAlign: "center" }}>
        <FiAlertTriangle size={36} color="#ef4444" style={{ marginBottom: "8px" }} />
        <h3>Request Not Found</h3>
        <p style={{ color: "#64748b", marginBottom: "1rem" }}>The requested ticket could not be found or has been removed.</p>
        <Link to="/escalations" className="btn btn-outline">
          Back to List
        </Link>
      </div>
    );
  }

  // Stepper calculations
  const steps = [
    { key: "submitted", label: "1. Submitted" },
    { key: "under_review", label: "2. Under Review" },
    { key: "in_progress", label: "3. Action Taken / In Progress" },
    { key: "resolved", label: "4. Resolved / Approved" },
  ];

  const getStepState = (stepKey) => {
    const s = request.status;
    if (s === "rejected") return stepKey === "submitted" ? "completed" : "rejected";
    if (s === "submitted") return stepKey === "submitted" ? "current" : "pending";
    if (s === "under_review") {
      if (stepKey === "submitted") return "completed";
      if (stepKey === "under_review") return "current";
      return "pending";
    }
    if (["action_taken", "in_progress", "partially_approved"].includes(s)) {
      if (["submitted", "under_review"].includes(stepKey)) return "completed";
      if (stepKey === "in_progress") return "current";
      return "pending";
    }
    if (["approved", "resolved", "closed"].includes(s)) {
      return "completed";
    }
    return "pending";
  };

  return (
    <div className="page-container" style={{ maxWidth: 1080, margin: "0 auto", padding: "1.5rem 1rem" }}>
      {/* ── Breadcrumbs ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#64748b", fontSize: "13px", marginBottom: "1rem" }}>
        <Link to={isSuperAdmin ? "/superadmin" : "/dashboard"} style={{ color: "#64748b" }}>
          {isSuperAdmin ? "SuperAdmin Hub" : "Dashboard"}
        </Link>
        <FiChevronRight size={13} />
        <Link to="/escalations" style={{ color: "#64748b" }}>Escalations & Requests</Link>
        <FiChevronRight size={13} />
        <span style={{ color: "var(--text, #1e293b)", fontWeight: 600 }}>{request.ticketNumber}</span>
      </div>

      {/* ── Ticket Header ── */}
      <div
        className="card"
        style={{
          padding: "1.25rem 1.5rem",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          marginBottom: "1.25rem",
          backgroundColor: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: 800,
                  fontSize: "14px",
                  color: "#1e293b",
                  backgroundColor: "#f1f5f9",
                  padding: "3px 8px",
                  borderRadius: "6px",
                }}
              >
                {request.ticketNumber}
              </span>
              <span
                style={{
                  padding: "3px 8px",
                  borderRadius: "4px",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  backgroundColor: request.type === "escalation" ? "#fef3c7" : "#e0e7ff",
                  color: request.type === "escalation" ? "#92400e" : "#3730a3",
                }}
              >
                {request.type === "escalation" ? "Matter Escalation" : "Resource Requisition"}
              </span>
              {getPriorityBadge(request.priority)}
              {getStatusBadge(request.status)}
            </div>

            <h1 style={{ fontSize: "20px", fontWeight: 800, margin: "6px 0 4px", color: "var(--text, #0f172a)" }}>
              {request.title}
            </h1>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12.5px", color: "#64748b", flexWrap: "wrap" }}>
              <span>
                <strong>College:</strong> {request.college?.name} ({request.college?.code})
              </span>
              <span>&bull;</span>
              <span>
                <strong>Category:</strong> {request.category}
              </span>
              <span>&bull;</span>
              <span>
                <strong>Submitted by:</strong> {request.requestedBy?.displayName || request.requestedBy?.username}
              </span>
              <span>&bull;</span>
              <span>
                {new Date(request.createdAt).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {isSuperAdmin && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowActionPanel((p) => !p)}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FiShield size={14} /> Take Super Admin Action
              </button>
            )}
            <Link to="/escalations" className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiArrowLeft size={14} /> Back
            </Link>
          </div>
        </div>

        {/* ── Status Progression Stepper ── */}
        <div style={{ marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid #f1f5f9" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem" }}>
            {steps.map((st) => {
              const state = getStepState(st.key);
              let barColor = "#e2e8f0";
              let textColor = "#94a3b8";
              let weight = 500;

              if (state === "completed") {
                barColor = "#10b981";
                textColor = "#047857";
                weight = 700;
              } else if (state === "current") {
                barColor = "#3b82f6";
                textColor = "#002747";
                weight = 700;
              } else if (state === "rejected") {
                barColor = "#ef4444";
                textColor = "#b91c1c";
                weight = 700;
              }

              return (
                <div key={st.key}>
                  <div
                    style={{
                      height: "5px",
                      borderRadius: "99px",
                      backgroundColor: barColor,
                      marginBottom: "6px",
                    }}
                  />
                  <div style={{ fontSize: "11.5px", color: textColor, fontWeight: weight }}>
                    {st.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Super Admin Action / Resolution Callout (Prominently displayed) ── */}
      {request.superAdminAction?.actionTaken ? (
        <div
          style={{
            backgroundColor: "#f5f3ff",
            border: "1.5px solid #c4b5fd",
            borderRadius: "12px",
            padding: "1.25rem 1.5rem",
            marginBottom: "1.25rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: "6px",
                backgroundColor: "#7c3aed",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiShield size={14} />
            </div>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "#5b21b6" }}>
              Super Admin Action & Resolution
            </div>
            {request.superAdminAction?.actionDate && (
              <span style={{ fontSize: "12px", color: "#7c3aed", marginLeft: "auto" }}>
                Action Date:{" "}
                {new Date(request.superAdminAction.actionDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </div>

          <div style={{ fontSize: "13.5px", color: "#1e1b4b", lineHeight: 1.6, marginBottom: "8px" }}>
            <strong>Action Taken / Directive:</strong> {request.superAdminAction.actionTaken}
          </div>

          {request.superAdminAction.allocatedResources && (
            <div
              style={{
                fontSize: "13px",
                backgroundColor: "#ede9fe",
                padding: "0.6rem 0.85rem",
                borderRadius: "8px",
                color: "#4338ca",
                marginTop: "6px",
              }}
            >
              <strong>Allocated Resources / Dispatch Info:</strong> {request.superAdminAction.allocatedResources}
            </div>
          )}

          {request.superAdminAction.actionBy?.displayName && (
            <div style={{ fontSize: "11.5px", color: "#6b21a8", marginTop: "8px" }}>
              Reviewed by: {request.superAdminAction.actionBy.displayName} (Super Admin)
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: "10px",
            padding: "0.85rem 1.25rem",
            marginBottom: "1.25rem",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "13px",
            color: "#92400e",
          }}
        >
          <FiClock size={18} style={{ flexShrink: 0 }} />
          <div>
            <strong>Awaiting Super Admin Action:</strong> This request has been submitted and is pending review by
            central administration. Any resolution or allocated resources will appear right here.
          </div>
        </div>
      )}

      {/* ── Super Admin Action Form Drawer / Panel (Visible when opened) ── */}
      {isSuperAdmin && showActionPanel && (
        <div
          className="card"
          style={{
            padding: "1.5rem",
            borderRadius: "12px",
            border: "2px solid #7c3aed",
            backgroundColor: "#faf5ff",
            marginBottom: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#4c1d95" }}>
              Super Admin Action Panel
            </div>
            <button
              type="button"
              onClick={() => setShowActionPanel(false)}
              className="btn btn-sm btn-outline"
            >
              Close
            </button>
          </div>

          <form onSubmit={handleSaveAction}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div>
                <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                  Update Ticket Status <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  value={actionStatus}
                  onChange={(e) => setActionStatus(e.target.value)}
                  className="form-control"
                  required
                >
                  <option value="under_review">Under Review</option>
                  <option value="action_taken">Action Taken</option>
                  <option value="in_progress">In Progress</option>
                  <option value="approved">Approved</option>
                  <option value="partially_approved">Partially Approved</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                  Decision Type
                </label>
                <select
                  value={actionDecision}
                  onChange={(e) => setActionDecision(e.target.value)}
                  className="form-control"
                >
                  <option value="approved">Approved</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="partially_approved">Partially Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="under_review">Under Review</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                Action Taken / Official Remarks / Directions <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Specify the resolution, official direction, or decision taken by the Super Admin..."
                value={actionTakenText}
                onChange={(e) => setActionTakenText(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12.5px" }}>
                Allocated Resources / Dispatch Tracking (Optional)
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 5 Desktops dispatched from Central Warehouse via DTDC tracking #881240"
                value={allocatedResourcesText}
                onChange={(e) => setAllocatedResourcesText(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowActionPanel(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={savingAction}
                style={{ backgroundColor: "#7c3aed", borderColor: "#7c3aed" }}
              >
                {savingAction ? "Saving..." : "Save & Update Status"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Main Content Grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "1.25rem", alignItems: "start" }}>
        {/* Left Column: Request Details & Items */}
        <div>
          {/* Request Details */}
          <div
            className="card"
            style={{
              padding: "1.25rem 1.5rem",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              marginBottom: "1.25rem",
            }}
          >
            <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "0.75rem" }}>
              {request.type === "escalation" ? "Escalated Matter Details" : "Requisition Description"}
            </div>
            <div style={{ fontSize: "13.5px", color: "#334155", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {request.description}
            </div>

            {request.impactJustification && (
              <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: "12.5px", fontWeight: 700, color: "#64748b", marginBottom: "4px" }}>
                  {request.type === "escalation"
                    ? "Why College Level Cannot Resolve / Administrative Justification:"
                    : "Operational & Academic Impact / Justification:"}
                </div>
                <div style={{ fontSize: "13px", color: "#475569", lineHeight: 1.5 }}>
                  {request.impactJustification}
                </div>
              </div>
            )}
          </div>

          {/* Itemized Requisition List (if Resource Request) */}
          {request.type === "resource_request" && (
            <div
              className="card"
              style={{
                padding: "1.25rem 1.5rem",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                marginBottom: "1.25rem",
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "0.75rem" }}>
                Requested Resources & Equipment List
              </div>

              <div className="table-wrapper">
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc", fontSize: "12px" }}>
                      <th>Item Name</th>
                      <th style={{ width: 60, textAlign: "center" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Est. Unit Cost</th>
                      <th style={{ textAlign: "right" }}>Total Cost</th>
                      <th>Specifications / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.itemsRequested?.map((item, index) => {
                      const total = (Number(item.quantity) || 1) * (Number(item.estimatedCost) || 0);
                      return (
                        <tr key={index}>
                          <td style={{ fontWeight: 600, fontSize: "13px" }}>{item.itemName}</td>
                          <td style={{ textAlign: "center", fontWeight: 700 }}>{item.quantity}</td>
                          <td style={{ textAlign: "right", fontSize: "12.5px" }}>
                            ₹{(Number(item.estimatedCost) || 0).toLocaleString("en-IN")}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 700, fontSize: "12.5px", color: "#003b67" }}>
                            ₹{total.toLocaleString("en-IN")}
                          </td>
                          <td style={{ fontSize: "12px", color: "#64748b" }}>
                            {item.specifications || item.urgencyReason || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Total Budget Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "1.5rem",
                  marginTop: "1rem",
                  paddingTop: "0.75rem",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Total Items Count:{" "}
                  <strong>{request.itemsRequested?.reduce((s, i) => s + (Number(i.quantity) || 1), 0) || 0}</strong>
                </div>
                <div style={{ fontSize: "15px", fontWeight: 800, color: "#1e293b" }}>
                  Total Requisition Budget:{" "}
                  <span style={{ color: "#003b67" }}>
                    ₹{(request.totalEstimatedCost || 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Activity Timeline & Conversation */}
        <div>
          <div
            className="card"
            style={{
              padding: "1.25rem 1.5rem",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: 700,
                color: "#1e293b",
                marginBottom: "1rem",
                paddingBottom: "0.6rem",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <FiMessageSquare size={16} color="#6366f1" /> Activity & Conversation Timeline
            </div>

            {/* Timeline Stream */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.25rem", maxHeight: 420, overflowY: "auto", paddingRight: "4px" }}>
              {request.timeline?.map((t, idx) => {
                const isSuperAdminSender = t.senderRole === "superadmin";
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: "0.6rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: isSuperAdminSender ? "#7c3aed" : "#3b82f6",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {isSuperAdminSender ? "SA" : "AD"}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        backgroundColor: isSuperAdminSender ? "#faf5ff" : "#f8fafc",
                        border: `1px solid ${isSuperAdminSender ? "#e9d5ff" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        padding: "0.6rem 0.8rem",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: isSuperAdminSender ? "#6b21a8" : "#1e293b" }}>
                          {t.senderName} {isSuperAdminSender && "(Super Admin)"}
                        </span>
                        <span style={{ fontSize: "10.5px", color: "#94a3b8" }}>
                          {new Date(t.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>

                      <div style={{ fontSize: "12.5px", color: "#334155", lineHeight: 1.45 }}>
                        {t.message}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reply / Comment Box */}
            <form onSubmit={handleAddComment}>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Post an update or reply..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  style={{ fontSize: "12.5px" }}
                  disabled={sendingComment}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={sendingComment || !commentText.trim()}
                  style={{ padding: "0 12px", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <FiSend size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
